import React, { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Briefcase, Loader2, Package, DollarSign, ShoppingCart } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import RegisterSaleModal from "./RegisterSaleModal";

const statusColors = {
  preparation: "bg-blue-100 text-blue-700 border-blue-200",
  submitted: "bg-purple-100 text-purple-700 border-purple-200",
  under_review: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  proposed: "bg-blue-100 text-blue-700 border-blue-200",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  in_progress: "bg-purple-100 text-purple-700 border-purple-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
  sold: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  delivered: "bg-emerald-100 text-emerald-700 border-emerald-200"
};

export default function CasesServicesTab({ contactId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [caseItems, setCaseItems] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRegisterSaleModal, setShowRegisterSaleModal] = useState(false);

  useEffect(() => {
    if (contactId) {
      loadData();
    }
  }, [contactId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Fetch REAL cases from cases table via case_contacts
      const { data: caseContacts, error: ccError } = await supabase
        .from('case_contacts')
        .select('case_id')
        .eq('contact_id', contactId);

      if (ccError) throw ccError;

      if (caseContacts && caseContacts.length > 0) {
        const caseIds = [...new Set(caseContacts.map(cc => cc.case_id))];
        
        const { data: casesData, error: casesError } = await supabase
          .from('cases')
          .select(`
            *,
            item:items(
              id, 
              unit_price, 
              total_amount, 
              status, 
              num_dependents,
              catalog(*),
              deal:deals(id, name, source)
            )
          `)
          .in('id', caseIds)
          .order('created_at', { ascending: false });

        if (casesError) throw casesError;
        setCaseItems(casesData || []);
      } else {
        setCaseItems([]);
      }

      // Fetch services directly from services table
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select(`
          *,
          item:items(*, catalog(*), deal:deals(name, id, source)),
          assigned_user:app_users!services_assigned_to_user_id_fkey(id, display_name)
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (servicesError) throw servicesError;
      setServiceItems(servicesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      setCaseItems([]);
      setServiceItems([]);
    }
    setIsLoading(false);
  };

  const getStatusLabel = (status) => {
    if (!status) return '';
    const labels = {
      pending: 'Pendente',
      in_progress: 'Em Andamento',
      delivered: 'Entregue',
      cancelled: 'Cancelado'
    };
    return labels[status] || t(`itemStatus.${status}`, status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  };

  const formatCurrency = amount => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      {/* Cases Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900">{t('casesServices.cases')}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('casesServices.casesTotal', { count: caseItems.length })}
          </p>
        </div>

        {caseItems.length > 0 ? (
          <div className="space-y-4">
            {caseItems.map(caseItem => (
              <Card 
                key={caseItem.id} 
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/CaseDetail?id=${caseItem.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <Briefcase className="w-5 h-5 text-slate-400 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">
                            {caseItem.visa_type || caseItem.item?.catalog?.name || t('casesServices.case')}
                          </h3>
                          {caseItem.item?.deal?.name && (
                            <p className="text-sm text-slate-500 mt-1">
                              {t('casesServices.deal')}: {caseItem.item.deal.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={`${statusColors[caseItem.status] || statusColors.preparation} border font-medium`}>
                          {getStatusLabel(caseItem.status)}
                        </Badge>
                        {caseItem.item?.deal?.source === 'pipeline' && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 border font-medium">
                            Pipeline
                          </Badge>
                        )}
                        {caseItem.item?.deal?.source === 'direct' && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border font-medium">
                            Venda Direta
                          </Badge>
                        )}
                      </div>
                    </div>
                    {caseItem.item?.total_amount && (
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-xl font-bold text-slate-900">
                          <DollarSign className="w-5 h-5" />
                          {formatCurrency(caseItem.item.total_amount)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    {caseItem.item?.unit_price && (
                      <div className="text-sm">
                        <p className="text-xs text-slate-500">{t('casesServices.unitPrice')}</p>
                        <p className="font-medium text-slate-700">{formatCurrency(caseItem.item.unit_price)}</p>
                      </div>
                    )}
                    {caseItem.item?.num_dependents > 0 && (
                      <div className="text-sm">
                        <p className="text-xs text-slate-500">{t('casesServices.dependents')}</p>
                        <p className="font-medium text-slate-700">{caseItem.item.num_dependents}</p>
                      </div>
                    )}
                    <div className="text-sm">
                      <p className="text-xs text-slate-500">{t('casesServices.added')}</p>
                      <p className="font-medium text-slate-700">
                        {caseItem.created_at ? safeFormat(new Date(caseItem.created_at), "MMM dd, yyyy") : "N/A"}
                      </p>
                    </div>
                  </div>

                  {caseItem.item?.catalog?.description && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-600 line-clamp-2">{caseItem.item.catalog.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium mb-1">{t('casesServices.noCasesYet')}</p>
            <p className="text-sm">{t('casesServices.caseItemsHint')}</p>
          </div>
        )}
      </div>

      {/* Services Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{t('casesServices.services')}</h2>
            <p className="text-sm text-slate-500 mt-1">
              {t('casesServices.servicesTotal', { count: serviceItems.length })}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setShowRegisterSaleModal(true)}
            className="flex items-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            Registrar Venda
          </Button>
        </div>

        {serviceItems.length > 0 ? (
          <div className="space-y-4">
            {serviceItems.map(service => (
              <Card 
                key={service.id} 
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/ServiceDetail?id=${service.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <Package className="w-5 h-5 text-slate-400 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">
                            {service.item?.catalog?.name || service.service_type || t('casesServices.service')}
                          </h3>
                          {service.item?.deal?.name && (
                            <p className="text-sm text-slate-500 mt-1">
                              {t('casesServices.deal')}: {service.item.deal.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={`${statusColors[service.status] || statusColors.proposed} border font-medium`}>
                          {getStatusLabel(service.status)}
                        </Badge>
                        {service.item?.deal?.source === 'pipeline' && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 border font-medium">
                            Pipeline
                          </Badge>
                        )}
                        {service.item?.deal?.source === 'direct' && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border font-medium">
                            Venda Direta
                          </Badge>
                        )}
                        {service.assigned_user?.display_name && (
                          <Badge variant="outline" className="font-medium">
                            {service.assigned_user.display_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-xl font-bold text-slate-900">
                        <DollarSign className="w-5 h-5" />
                        {formatCurrency(service.item?.unit_price)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <div className="text-sm">
                      <p className="text-xs text-slate-500">{t('casesServices.added')}</p>
                      <p className="font-medium text-slate-700">
                        {service.created_at ? safeFormat(new Date(service.created_at), "MMM dd, yyyy") : "N/A"}
                      </p>
                    </div>
                    {service.delivery_date && (
                      <div className="text-sm">
                        <p className="text-xs text-slate-500">Data de Entrega</p>
                        <p className="font-medium text-slate-700">
                          {safeFormat(new Date(service.delivery_date), "MMM dd, yyyy")}
                        </p>
                      </div>
                    )}
                  </div>

                  {service.item?.catalog?.description && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-600 line-clamp-2">{service.item.catalog.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium mb-1">{t('casesServices.noServicesYet')}</p>
            <p className="text-sm">{t('casesServices.serviceItemsHint')}</p>
          </div>
        )}
      </div>

      {/* Register Sale Modal */}
      <RegisterSaleModal
        open={showRegisterSaleModal}
        onOpenChange={setShowRegisterSaleModal}
        contactId={contactId}
        onSuccess={loadData}
      />
    </div>
  );
}
